// ExcelJS is ~250 kB gzipped, so it's loaded on demand (export click / file
// pick) rather than bundled into the page.

export type XlsxCell = string | number | null | undefined;

const XLSX_MIME =
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';

function hexToRgb(hex: string): [number, number, number] {
  const m = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  if (!m) return [0, 165, 134]; // Safe's #00a586
  return [parseInt(m[1], 16), parseInt(m[2], 16), parseInt(m[3], 16)];
}

function argb([r, g, b]: [number, number, number]): string {
  return `FF${[r, g, b].map((v) => Math.round(v).toString(16).padStart(2, '0')).join('')}`.toUpperCase();
}

/** WCAG relative luminance, 0 (black) – 1 (white). */
function luminance([r, g, b]: [number, number, number]): number {
  const lin = (v: number) => {
    const c = v / 255;
    return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
  };
  return 0.2126 * lin(r) + 0.7152 * lin(g) + 0.0722 * lin(b);
}

/**
 * Builds an .xlsx client-side and triggers a browser download: brand-coloured
 * bold header (black or white text, whichever contrasts more), frozen header
 * with filter buttons, and zebra rows in a light brand tint. Excel fills have
 * no alpha, so the tint is the brand mixed 12% into white. Stripes are a
 * conditional format on ROW() so they survive sorting in Excel (filtering can
 * still put two tinted rows next to each other).
 */
export async function downloadXlsx(
  filename: string,
  sheetName: string,
  rows: XlsxCell[][],
  brandColor: string,
): Promise<void> {
  const { Workbook } = await import('exceljs');
  const wb = new Workbook();
  // Sheet names: max 31 chars, no []:*?/\
  const ws = wb.addWorksheet(
    sheetName.replace(/[[\]:*?/\\]/g, ' ').slice(0, 31),
    {
      views: [{ state: 'frozen', ySplit: 1 }],
    },
  );
  ws.addRows(rows.map((r) => r.map((v) => v ?? '')));

  const brand = hexToRgb(brandColor);
  const textOnBrand = luminance(brand) > 0.179 ? 'FF000000' : 'FFFFFFFF';
  const tint = argb(brand.map((v) => 255 - (255 - v) * 0.12) as typeof brand);
  const columnCount = Math.max(1, ...rows.map((r) => r.length));

  const header = ws.getRow(1);
  for (let c = 1; c <= columnCount; c++) {
    const cell = header.getCell(c);
    cell.font = { bold: true, color: { argb: textOnBrand } };
    cell.fill = {
      type: 'pattern',
      pattern: 'solid',
      fgColor: { argb: argb(brand) },
    };
  }

  const lastCol = ws.getColumn(columnCount).letter;
  ws.autoFilter = `A1:${lastCol}1`;
  if (rows.length > 1) {
    ws.addConditionalFormatting({
      ref: `A2:${lastCol}${rows.length}`,
      rules: [
        {
          type: 'expression',
          priority: 1,
          formulae: ['MOD(ROW(),2)=1'],
          style: {
            fill: {
              type: 'pattern',
              pattern: 'solid',
              bgColor: { argb: tint },
            },
          },
        },
      ],
    });
  }

  for (let c = 1; c <= columnCount; c++) {
    const longest = Math.max(...rows.map((r) => String(r[c - 1] ?? '').length));
    // +4 leaves room for the header's filter button.
    ws.getColumn(c).width = Math.min(50, Math.max(8, longest + 4));
  }

  const buf = await wb.xlsx.writeBuffer();
  const url = URL.createObjectURL(new Blob([buf], { type: XLSX_MIME }));
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

/** The first worksheet of an .xlsx as rows of display text. */
export async function readXlsxRows(file: File): Promise<string[][]> {
  const { Workbook } = await import('exceljs');
  const wb = new Workbook();
  await wb.xlsx.load(await file.arrayBuffer());
  const ws = wb.worksheets[0];
  if (!ws) return [];
  const rows: string[][] = [];
  ws.eachRow((row) => {
    const cells: string[] = [];
    for (let c = 1; c <= row.cellCount; c++) cells.push(row.getCell(c).text);
    rows.push(cells);
  });
  return rows;
}

/** "Användarvalda företag" -> "anvandarvalda-foretag" */
export function toFileSlug(s: string): string {
  return s
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');
}
